import React, { FunctionComponent, useState } from 'react';
import { useFormik } from 'formik';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import moment from 'moment';
import { map } from 'ramda';
import { useSwipeable } from 'react-swipeable';
import { IBindingCallback1, IBindingAction } from '../../../../common/models/callback';
import { editReferenceValidationSchema } from '../../../../common/validation/yup';
import RegularButton from '../../../../components/RegularButton';
import Input from '../../../../components/Input';
import { IReferenceCandidateForm } from '../../../../common/models/reference/IReferenceCandidateForm';
import { IAppState } from '../../../../common/models/store/IAppState';
import { ReferenceType } from '../../../../common/enums/ReferenceType';
import { referenceExists, addThemselves, errorMessages } from './utils';
import { IContactMainInfo } from '../../../../common/models/contact/IContactMainInfo';
import { ErrorMessage as Error } from '../../../../components/ErrorMessage';
import { ReactComponent as Check } from '../../../../assets/icons/check.svg';
import CustomSelect from '../../../../components/CustomSelect';
import { refTypes } from '../../../../components/ReferenceRoleForm/utils';
import { messages } from '../ReferenceCard/utils';
import { stringToDate } from '../../../../common/helpers/dateHelper';
import { checkPhoneNumberRoutine } from '../../routines';
import LoaderWrapper from '../../../../components/LoaderWrapper';

interface IReferenceNameFormProps {
  candidate: IContactMainInfo;
  reference: IReferenceCandidateForm;
  references: IReferenceCandidateForm[];
  onCancel: IBindingAction;
  isCheckingPhoneNumber: boolean;
  invalidPhoneNumber?: string;
  onSaveChanges: IBindingCallback1<IReferenceCandidateForm>;
  checkPhoneNumber: IBindingCallback1<{ onSuccess: IBindingAction; phoneNumber: string }>
}

type EditReferenceFormikValues = Omit<IReferenceCandidateForm, 'workStartDate' | 'workEndDate'> & {
  workStartDate: string; workEndDate: string; }

const EditReferenceForm: FunctionComponent<IReferenceNameFormProps> = ({
  candidate,
  reference,
  references,
  invalidPhoneNumber,
  isCheckingPhoneNumber,
  checkPhoneNumber,
  onCancel,
  onSaveChanges
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const initialValues = {
    id: reference.id,
    contatId: reference.contatId,
    type: reference.type || ReferenceType.Manager,
    otherType: reference.otherType,
    firstName: reference.firstName,
    lastName: reference.lastName,
    phoneNumber: reference.phoneNumber || '',
    email: reference.email || '',
    workInCompany: reference.workInCompany || '',
    workStartDate: reference.workStartDate ? moment(reference.workStartDate).format('MMyyyy') : '',
    workEndDate: reference.workEndDate ? moment(reference.workEndDate).format('MMyyyy') : ''
  };
  const getErrors = (values: EditReferenceFormikValues) => (
    <div>
      {referenceExists(values as IContactMainInfo, references) && <Error {...errorMessages.existed1} />}
      {addThemselves(values as IContactMainInfo, candidate) && <Error {...errorMessages.existed2} />}
      {Boolean(values.phoneNumber) && invalidPhoneNumber === values.phoneNumber
      && <Error {...errorMessages.unreachablePhone} />}
    </div>
  );

  const isFormValid = (values: EditReferenceFormikValues, valid: boolean) => (
    valid
    && !referenceExists(values as IContactMainInfo, references)
    && !addThemselves(values as IContactMainInfo, candidate)
    && (!values.phoneNumber || values.phoneNumber !== invalidPhoneNumber)
  );

  const save = (values: EditReferenceFormikValues) => {
    const {
      email,
      phoneNumber,
      workStartDate,
      workEndDate,
      type,
      otherType,
      ...rest
    } = values;
    setIsSubmitting(true);
    if (isFormValid(values, true)) {
      const payload = {
        ...rest,
        type,
        email: email || null,
        phoneNumber: phoneNumber || null,
        workStartDate: stringToDate(workStartDate),
        workEndDate: workEndDate ? stringToDate(workEndDate) : null,
        otherType: type === ReferenceType.Other ? otherType : undefined
      } as IReferenceCandidateForm;
      if (payload.phoneNumber && payload.phoneNumber !== reference.phoneNumber) {
        checkPhoneNumber({ onSuccess: () => onSaveChanges(payload), phoneNumber: payload.phoneNumber });
      } else {
        onSaveChanges(payload);
      }
    }
  };

  const { handleSubmit, setFieldValue, values, errors, isValid } = useFormik({
    initialValues,
    onSubmit: save,
    validationSchema: editReferenceValidationSchema,
    validateOnMount: true,
    validateOnChange: true
  });
  const isValidValues = isFormValid(values, isValid);

  const swipeHandler = useSwipeable({
    onSwipedLeft: () => handleSubmit(),
    onSwipedRight: onCancel,
    trackMouse: false
  });

  const isPhoneNumberValid = !errors.phoneNumber && values.phoneNumber !== invalidPhoneNumber;

  return (
    <div {...swipeHandler} className="cf-container position-relative cf-container-with-bttns">
      <LoaderWrapper transparent loading={isCheckingPhoneNumber}>
        <div className="cf-title">
          <FormattedMessage id="candidateForm.edit.title" defaultMessage="Edit reference" />
        </div>
        <form onSubmit={handleSubmit} className="w-100">
          <div className="cf-form">
            <div className="cf-inputs-container">
              <Input
                type="text"
                label={{ id: 'firstName', defaultMessage: 'First name' }}
                value={values.firstName}
                success={isSubmitting && !errors.firstName && Boolean(values.firstName)}
                onChange={v => setFieldValue('firstName', v)}
                variant="underlined"
                size="lg"
              />
              <Input
                type="text"
                label={{ id: 'lastName', defaultMessage: 'Last name' }}
                value={values.lastName}
                success={isSubmitting && !errors.lastName && Boolean(values.lastName)}
                onChange={v => setFieldValue('lastName', v)}
                size="lg"
                variant="underlined"
              />
            </div>
            <Input
              type="text"
              label={{ id: 'email', defaultMessage: 'Email' }}
              value={values.email as string}
              error={isSubmitting && Boolean(errors.email && values.email)}
              success={isSubmitting && !errors.email && Boolean(values.email)}
              onChange={v => setFieldValue('email', v)}
              size="lg"
              variant="underlined"
            />
            <Input
              type="phoneNumber"
              label={{ id: 'survey.refPhoneNumber', defaultMessage: 'Mobile phone number' }}
              value={values.phoneNumber as string}
              error={isSubmitting && Boolean(values.phoneNumber) && !isPhoneNumberValid}
              success={isSubmitting && Boolean(values.phoneNumber) && isPhoneNumberValid}
              onChange={val => setFieldValue('phoneNumber', val)}
              size="lg"
              variant="underlined"
            />
            <CustomSelect
              label={{ id: 'contactProfile.relationship', defaultMessage: 'Relationship' }}
              onChange={val => setFieldValue('type', val)}
              value={values.type as ReferenceType}
              options={map(value => ({
                label: messages[value],
                value
              }), refTypes)}
              variant="radio"
              triggerVariant="underlined"
            />
            {values.type === ReferenceType.Other && (
              <Input
                type="text"
                label={{
                  id: 'candidateForm.relationship.other',
                  defaultMessage: 'Please specify the reference’s relationship to you'
                }}
                value={values.otherType}
                onChange={val => setFieldValue('otherType', val)}
                variant="underlined"
                size="lg"
              />
            )}
            <Input
              type="text"
              label={{ id: 'candidateForm.company', defaultMessage: 'Company name' }}
              value={values.workInCompany as string}
              error={isSubmitting && Boolean(errors.workInCompany && values.workInCompany)}
              onChange={v => setFieldValue('workInCompany', v)}
              variant="underlined"
              size="lg"
            />
            <div className="cf-inputs-container">
              <Input
                type="date"
                label={{ id: 'candidateForm.workStartDate', defaultMessage: 'Start date' }}
                value={values.workStartDate}
                error={isSubmitting && Boolean(errors.workStartDate && values.workStartDate)}
                success={isSubmitting && !errors.workStartDate && Boolean(values.workStartDate)}
                onChange={val => setFieldValue('workStartDate', val)}
                variant="underlined"
                size="lg"
              />
              <Input
                type="date"
                label={{ id: 'candidateForm.lastDate', defaultMessage: 'End date' }}
                value={values.workEndDate}
                error={isSubmitting && Boolean(errors.workEndDate && values.workEndDate)}
                success={isSubmitting && !errors.workEndDate && Boolean(values.workEndDate)}
                onChange={val => setFieldValue('workEndDate', val)}
                variant="underlined"
                size="lg"
              />
            </div>
            {getErrors(values)}
          </div>
          <div className="cf-buttons m-0">
            <RegularButton
              size="lg"
              variant="secondary"
              textIntlId="candidateForm.edit.cancel"
              defaultMessage="Cancel"
              onClick={onCancel}
            />
            <RegularButton
              enableAction
              disabled={!isValidValues}
              size="lg"
              variant="primary"
              icon={<Check />}
              iconPosition="right"
              textIntlId="candidateForm.edit.save"
              defaultMessage="Save changes"
              onClick={() => {
                setIsSubmitting(true);
                if (isValidValues) {
                  handleSubmit();
                }
              }}
              type="submit"
            />
          </div>
        </form>
      </LoaderWrapper>
    </div>
  );
};

const mapStateToProps = ({
  referenceRequest: {
    references,
    updateReference,
    candidateId,
    firstName,
    lastName,
    phoneNumber,
    email,
    isCheckingPhoneNumber,
    invalidPhoneNumber
  }
}: IAppState) => ({
  references,
  reference: updateReference,
  candidate: { id: candidateId, firstName, lastName, phoneNumber, email },
  isCheckingPhoneNumber,
  invalidPhoneNumber
});

const mapDispachToProps = {
  checkPhoneNumber: checkPhoneNumberRoutine
};

export default connect(mapStateToProps, mapDispachToProps)(EditReferenceForm);
